import React, { useState } from 'react';
import toast from 'react-hot-toast';
import { updateBrandDealBrief } from '../../api/payment/brand-deal.api';
import type { BrandDealSummary, BrandTaskBrief } from '../../api/payment/brand-deal.api';
import { dealStatusLabel } from '../../utils/brand-deal';
import { BRIEF_EDIT_PRICE_NOTE, BRIEF_TEXT_MAX_LENGTH, briefVersionLabel, validateBriefText } from '../../utils/brand-brief-invite';
import { BrandThreadToggle } from './brand-thread-panel';
import EditedAfterReviewBadge from './edited-after-review-badge';

// Lời mời làm brief phía người đặt (spec 2026-10-12 §7). Mọi component khai ở cấp module để ô chữ không bị dựng lại khi trang tải lại.

const errorMessage = (err: unknown, fallback: string) =>
  (err as { response?: { data?: { message?: string } } })?.response?.data?.message || fallback;

const fmtVnd = (n: number) => `${Number(n || 0).toLocaleString('vi-VN')}đ`;

const formatDate = (iso?: string | null) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString('vi-VN', { day: '2-digit', month: '2-digit', year: 'numeric' });
};

/** Hộp sửa brief của một lời mời còn chờ creator trả lời. */
const BrandBriefInviteEditor: React.FC<{ deal: BrandDealSummary; onClose: () => void; onSaved: () => void }> = ({
  deal,
  onClose,
  onSaved,
}) => {
  const current = deal.briefText ?? '';
  const [text, setText] = useState(current);
  const [serverError, setServerError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const localError = validateBriefText(text, current);
  const changed = text.trim() !== current.trim();
  const shownError = serverError ?? (changed ? localError : null);
  const inputId = `brief-${deal.id}`;

  const save = async () => {
    if (localError) return;
    setSaving(true);
    setServerError(null);
    try {
      const res = await updateBrandDealBrief(deal.id, text.trim());
      toast.success(`Đã lưu brief bản ${res.briefVersion}.`);
      onSaved();
    } catch (e) {
      setServerError(errorMessage(e, 'Không lưu được brief.'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="ui-divider flex flex-col gap-2 rounded-lg border p-3">
      <div className="text-[14px] font-semibold ui-text">Sửa brief cho lời mời này</div>
      <label htmlFor={inputId} className="text-[13px] ui-text">
        Brief
      </label>
      <textarea
        id={inputId}
        rows={6}
        value={text}
        maxLength={BRIEF_TEXT_MAX_LENGTH}
        onChange={(e) => {
          setText(e.target.value);
          setServerError(null);
        }}
        className="w-full rounded-md border ui-divider bg-transparent px-3 py-2 text-[14px] outline-none ui-text"
      />
      <p className="text-[12px] ui-muted">{BRIEF_EDIT_PRICE_NOTE}</p>
      {shownError && <p className="text-[12px] text-rose-600 dark:text-rose-400">{shownError}</p>}
      <div className="flex gap-2">
        <button
          type="button"
          disabled={saving || !!localError}
          onClick={save}
          className="rounded-md bg-[#0057FF] px-3 py-1.5 text-sm font-semibold text-white disabled:opacity-60"
        >
          {saving ? 'Đang lưu…' : 'Lưu brief'}
        </button>
        <button type="button" disabled={saving} onClick={onClose} className="rounded-md border ui-divider px-3 py-1.5 text-sm ui-text">
          Huỷ
        </button>
      </div>
    </div>
  );
};

/** Khối "Lời mời làm brief" ở chi tiết chiến dịch brief: chỉ lời mời chưa nhận; đã nhận thì nằm ở tab Nhiệm vụ. */
export const BrandBriefInviteList: React.FC<{
  deals: BrandDealSummary[];
  editingId: string | null;
  cancelBusyId: string | null;
  onEdit: (deal: BrandDealSummary) => void;
  onCloseEditor: () => void;
  onSaved: () => void;
  onCancel: (deal: BrandDealSummary) => void;
}> = ({ deals, editingId, cancelBusyId, onEdit, onCloseEditor, onSaved, onCancel }) => {
  const invites = deals.filter((d) => d.isBriefInvite && !d.acceptedAt);
  return (
    <div className="space-y-3 rounded-xl border ui-divider p-4">
      <div className="font-semibold ui-text">Lời mời làm brief</div>
      <p className="text-xs ui-muted">Lời mời creator đã nhận chuyển sang mục Nhiệm vụ.</p>
      {invites.length === 0 ? (
        <p className="text-sm ui-muted">Chưa mời creator nào làm brief.</p>
      ) : (
        invites.map((d) => {
          const version = briefVersionLabel(d.briefVersion);
          return (
            <div key={d.id} className="flex flex-col gap-2 rounded-md border ui-divider p-2.5 text-sm">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div className="flex flex-wrap items-center gap-x-4 gap-y-1 ui-text">
                  <span className="font-semibold">{dealStatusLabel(d.status)}</span>
                  <span>Giá {fmtVnd(d.priceVnd)}</span>
                  {d.status === 'Invited' && d.respondBy && <span className="ui-muted">Hạn trả lời: {formatDate(d.respondBy)}</span>}
                  {version && <span className="ui-muted">{version}</span>}
                </div>
                <div className="flex shrink-0 gap-2">
                  {d.canEditBrief && editingId !== d.id && (
                    <button
                      type="button"
                      onClick={() => onEdit(d)}
                      className="rounded-md border border-[#0057FF] px-3 py-1 text-xs font-semibold text-[#0057FF]"
                    >
                      Sửa brief
                    </button>
                  )}
                  {d.status === 'Invited' && (
                    <button
                      type="button"
                      disabled={cancelBusyId === d.id}
                      onClick={() => onCancel(d)}
                      className="rounded-md border border-rose-200 px-3 py-1 text-xs font-semibold text-rose-600 disabled:opacity-50 dark:border-rose-900/40"
                    >
                      Huỷ lời mời
                    </button>
                  )}
                </div>
              </div>
              {editingId === d.id && <BrandBriefInviteEditor deal={d} onClose={onCloseEditor} onSaved={onSaved} />}
              <BrandThreadToggle threadId={d.threadId} />
            </div>
          );
        })
      )}
    </div>
  );
};

/** Khối brief riêng của lời mời ở màn duyệt bài (người đặt, admin). */
export const BrandDealBriefBox: React.FC<{ brief: BrandTaskBrief; showEditedBadge: boolean }> = ({ brief, showEditedBadge }) => {
  const version = briefVersionLabel(brief.version);
  return (
    <div className="ui-card flex flex-col gap-2 p-4">
      <div className="flex flex-wrap items-center gap-2">
        <h3 className="ui-heading text-[15px] font-bold">Brief của lời mời</h3>
        {version && <span className="ui-muted text-[12px]">{version}</span>}
        {showEditedBadge && <EditedAfterReviewBadge />}
      </div>
      <p className="ui-text whitespace-pre-wrap text-[14px]">{brief.briefText}</p>
    </div>
  );
};

export default BrandBriefInviteEditor;
